import axios from 'axios';
import type { AxiosInstance, AxiosError } from 'axios';
import type {
  ApiResponse,
  LoginRequest,
  LoginResponse,
  RegisterRequest,
  UserProfile,
  Asset,
  Order,
  WalletBalance,
  TradingPair,
  OrderBook,
  Ticker,
  PaginatedResponse,
  Session,
  UserSettings,
  UpdateSettingsRequest,
  ApiKey,
  CreateApiKeyRequest,
  CreateApiKeyResponse,
  SecurityEvent,
  LoginAttempt,
  Setup2FAResponse,
  Enable2FAResponse,
} from '@/shared';

class ApiClient {
  private client: AxiosInstance;
  private accessToken: string | null = null;
  private csrfToken: string | null = null;

  constructor() {
    // Use environment variable for API URL, fallback to /api for dev proxy
    const baseURL = import.meta.env.VITE_API_URL || '/api';

    this.client = axios.create({
      baseURL,
      timeout: 30000,
      headers: {
        'Content-Type': 'application/json',
      },
      // CRITICAL: Include credentials to send/receive HTTPOnly cookies
      withCredentials: true,
    });

    // Request interceptor to add CSRF token for state-changing requests
    this.client.interceptors.request.use(config => {
      // Add CSRF token for state-changing requests
      if (['POST', 'PUT', 'PATCH', 'DELETE'].includes(config.method?.toUpperCase() || '')) {
        const csrfToken = this.csrfToken || this.getCsrfTokenFromCookie();
        if (csrfToken) {
          config.headers['X-CSRF-Token'] = csrfToken;
        }
      }

      // Add access token if available (for API clients)
      if (this.accessToken) {
        config.headers['Authorization'] = `Bearer ${this.accessToken}`;
      }

      return config;
    });

    // Response interceptor for error handling and token refresh
    this.client.interceptors.response.use(
      response => {
        // Check for new access token in header (from cookie-based auth refresh)
        const newAccessToken = response.headers['x-access-token'];
        if (newAccessToken) {
          this.accessToken = newAccessToken;
        }
        return response;
      },
      async (error: AxiosError<ApiResponse>) => {
        if (error.response?.status === 401) {
          // Clear tokens
          this.accessToken = null;
          this.csrfToken = null;

          // Don't redirect if:
          // 1. Already on auth pages (login, register, etc.)
          // 2. This is a profile check during initialization
          const currentPath = window.location.pathname;
          const isAuthPage = currentPath.startsWith('/auth/');
          const isProfileCheck = error.config?.url?.includes('/user/profile');

          if (!isAuthPage && !isProfileCheck) {
            // Session expired during normal usage, redirect to login
            window.location.href = '/auth/login';
          }
        }
        return Promise.reject(error);
      }
    );
  }

  // Get CSRF token from cookie (set by server with httpOnly: false)
  // Supports multiple cookie names for different environments:
  // - csrf_token: New production name (cross-subdomain compatible)
  // - __Host-csrf_token: Legacy production name
  // - tf_csrf_token: Development name
  private getCsrfTokenFromCookie(): string | null {
    // Try new cross-subdomain cookie first (production with domain attribute)
    const crossDomainMatch = document.cookie.match(/(?:^|;\s*)csrf_token=([^;]*)/);
    if (crossDomainMatch) {
      return decodeURIComponent(crossDomainMatch[1]);
    }
    // Try legacy secure cookie (production without domain attribute)
    const secureMatch = document.cookie.match(/(?:^|;\s*)__Host-csrf_token=([^;]*)/);
    if (secureMatch) {
      return decodeURIComponent(secureMatch[1]);
    }
    // Fall back to non-secure cookie (development)
    const devMatch = document.cookie.match(/(?:^|;\s*)tf_csrf_token=([^;]*)/);
    return devMatch ? decodeURIComponent(devMatch[1]) : null;
  }

  // Set tokens after login (access token from response, CSRF from cookie or response)
  setTokens(accessToken: string | null, csrfToken?: string | null) {
    this.accessToken = accessToken;
    if (csrfToken !== undefined) {
      this.csrfToken = csrfToken;
    }
  }

  // Legacy method for backwards compatibility
  setToken(token: string | null) {
    this.accessToken = token;
  }

  // Clear tokens on logout
  clearTokens() {
    this.accessToken = null;
    this.csrfToken = null;
  }

  // Auth endpoints
  auth = {
    login: async (data: LoginRequest): Promise<LoginResponse> => {
      const response = await this.client.post<ApiResponse<LoginResponse>>('/auth/login', data);
      const result = response.data.data!;

      // Store tokens from response
      if (result.access_token) {
        this.accessToken = result.access_token;
      }
      if (result.csrf_token) {
        this.csrfToken = result.csrf_token;
      }

      return result;
    },

    register: async (data: RegisterRequest): Promise<{ message: string; user_id: string; email_verified?: boolean }> => {
      const response = await this.client.post<ApiResponse<{ message: string; user_id: string; email_verified?: boolean }>>('/auth/register', data);
      return response.data.data!;
    },

    checkUsername: async (username: string): Promise<{ available: boolean }> => {
      const response = await this.client.get<ApiResponse<{ available: boolean }>>('/auth/check-username', {
        params: { username },
      });
      return response.data.data!;
    },

    checkEmail: async (email: string): Promise<{ available: boolean }> => {
      const response = await this.client.get<ApiResponse<{ available: boolean }>>('/auth/check-email', {
        params: { email },
      });
      return response.data.data!;
    },

    verify2FA: async (twoFactorToken: string, code: string, method: 'totp' | 'email' | 'sms' = 'totp'): Promise<LoginResponse> => {
      const response = await this.client.post<ApiResponse<LoginResponse>>('/auth/verify-2fa', {
        two_factor_token: twoFactorToken,
        code,
        method,
      });
      const result = response.data.data!;

      // Store tokens from response
      if (result.access_token) {
        this.accessToken = result.access_token;
      }
      if (result.csrf_token) {
        this.csrfToken = result.csrf_token;
      }

      return result;
    },

    logout: async (): Promise<void> => {
      try {
        await this.client.post('/auth/logout');
      } finally {
        this.clearTokens();
      }
    },

    logoutAll: async (): Promise<void> => {
      try {
        await this.client.post('/auth/logout-all');
      } finally {
        this.clearTokens();
      }
    },

    verifyEmail: async (token: string): Promise<{ message: string }> => {
      const response = await this.client.get<ApiResponse<{ message: string }>>(`/auth/verify-email/${token}`);
      return response.data.data!;
    },

    resendVerification: async (email: string, captchaToken?: string): Promise<{ message: string }> => {
      const response = await this.client.post<ApiResponse<{ message: string }>>('/auth/resend-verification', {
        email,
        captcha_token: captchaToken,
      });
      return response.data.data!;
    },

    forgotPassword: async (email: string, captchaToken?: string): Promise<{ message: string }> => {
      const response = await this.client.post<ApiResponse<{ message: string }>>('/auth/forgot-password', {
        email,
        method: 'email',
        captcha_token: captchaToken,
      });
      return response.data.data!;
    },

    forgotPasswordSMS: async (phone: string, captchaToken?: string): Promise<{
      message: string;
      recovery_token?: string;
      expires_at?: string;
      masked_phone?: string;
    }> => {
      const response = await this.client.post<ApiResponse<any>>('/auth/forgot-password', {
        phone,
        method: 'sms',
        captcha_token: captchaToken,
      });
      return response.data.data!;
    },

    verifyRecoveryCode: async (recoveryToken: string, code: string): Promise<{ message: string; reset_token: string }> => {
      const response = await this.client.post<ApiResponse<any>>('/auth/verify-recovery-code', {
        recovery_token: recoveryToken,
        code,
      });
      return response.data.data!;
    },

    resetPassword: async (token: string, newPassword: string): Promise<{ message: string }> => {
      const response = await this.client.post<ApiResponse<{ message: string }>>('/auth/reset-password', {
        token,
        new_password: newPassword,
      });
      return response.data.data!;
    },

    // Security capabilities (2FA methods, recovery methods)
    getSecurityCapabilities: async (): Promise<{
      two_factor_methods: Array<{ type: string; name: string; description: string }>;
      recovery_methods: Array<{ type: string; name: string; description: string }>;
      captcha_required: boolean;
      email_verification_required: boolean;
    }> => {
      const response = await this.client.get<ApiResponse<any>>('/auth/security-capabilities');
      return response.data.data!;
    },

    // 2FA login flow
    send2FACode: async (twoFactorToken: string, method: 'email' | 'sms'): Promise<{
      message: string;
      expires_at: string;
      masked_email?: string;
      masked_phone?: string;
    }> => {
      const response = await this.client.post<ApiResponse<any>>('/auth/send-2fa-code', {
        two_factor_token: twoFactorToken,
        method,
      });
      return response.data.data!;
    },
  };

  // User endpoints
  user = {
    getProfile: async (): Promise<UserProfile> => {
      const response = await this.client.get<ApiResponse<UserProfile>>('/user/profile');
      return response.data.data!;
    },

    updateProfile: async (data: Partial<UserProfile>): Promise<void> => {
      await this.client.patch('/user/profile', data);
    },

    getSettings: async (): Promise<UserSettings> => {
      const response = await this.client.get<ApiResponse<UserSettings>>('/user/settings');
      return response.data.data!;
    },

    updateSettings: async (data: UpdateSettingsRequest): Promise<UserSettings> => {
      const response = await this.client.put<ApiResponse<UserSettings>>('/user/settings', data);
      return response.data.data!;
    },

    changePassword: async (currentPassword: string, newPassword: string): Promise<void> => {
      await this.client.post('/user/change-password', {
        current_password: currentPassword,
        new_password: newPassword,
      });
    },

    // Sessions
    getSessions: async (): Promise<Session[]> => {
      const response = await this.client.get<ApiResponse<Session[]>>('/user/sessions');
      return response.data.data!;
    },

    revokeSession: async (sessionId: string): Promise<void> => {
      await this.client.delete(`/user/sessions/${sessionId}`);
    },

    // 2FA
    setup2FA: async (): Promise<Setup2FAResponse> => {
      const response = await this.client.post<ApiResponse<Setup2FAResponse>>('/user/2fa/setup');
      return response.data.data!;
    },

    enable2FA: async (code: string): Promise<Enable2FAResponse> => {
      const response = await this.client.post<ApiResponse<Enable2FAResponse>>('/user/2fa/enable', { code });
      return response.data.data!;
    },

    disable2FA: async (code: string, password: string): Promise<void> => {
      await this.client.post('/user/2fa/disable', { code, password });
    },

    regenerateBackupCodes: async (code: string, password: string): Promise<{ backup_codes: string[] }> => {
      const response = await this.client.post<ApiResponse<{ backup_codes: string[] }>>('/user/2fa/regenerate-backup-codes', {
        code,
        password,
      });
      return response.data.data!;
    },

    // 2FA Status
    get2FAStatus: async (): Promise<{
      totp: { enabled: boolean; available: boolean };
      email: { enabled: boolean; available: boolean; email: string | null };
      sms: { enabled: boolean; available: boolean; phone: string | null; phone_verified: boolean };
    }> => {
      const response = await this.client.get<ApiResponse<any>>('/user/2fa/status');
      return response.data.data!;
    },

    // Email 2FA
    enableEmail2FA: async (): Promise<{ message: string; expires_at: string; masked_email: string }> => {
      const response = await this.client.post<ApiResponse<any>>('/user/2fa/email/enable');
      return response.data.data!;
    },

    confirmEmail2FA: async (code: string): Promise<{ message: string }> => {
      const response = await this.client.post<ApiResponse<any>>('/user/2fa/email/confirm', { code });
      return response.data.data!;
    },

    disableEmail2FA: async (code: string, password: string): Promise<{ message: string }> => {
      const response = await this.client.post<ApiResponse<any>>('/user/2fa/email/disable', { code, password });
      return response.data.data!;
    },

    // SMS 2FA
    enableSMS2FA: async (): Promise<{ message: string; expires_at: string; masked_phone: string }> => {
      const response = await this.client.post<ApiResponse<any>>('/user/2fa/sms/enable');
      return response.data.data!;
    },

    confirmSMS2FA: async (code: string): Promise<{ message: string }> => {
      const response = await this.client.post<ApiResponse<any>>('/user/2fa/sms/confirm', { code });
      return response.data.data!;
    },

    disableSMS2FA: async (code: string, password: string): Promise<{ message: string }> => {
      const response = await this.client.post<ApiResponse<any>>('/user/2fa/sms/disable', { code, password });
      return response.data.data!;
    },

    // Phone Management
    updatePhone: async (phone: string): Promise<{ message: string; phone: string }> => {
      const response = await this.client.post<ApiResponse<any>>('/user/phone/update', { phone });
      return response.data.data!;
    },

    sendPhoneCode: async (): Promise<{ message: string; expires_at: string; masked_phone: string }> => {
      const response = await this.client.post<ApiResponse<any>>('/user/phone/send-code');
      return response.data.data!;
    },

    verifyPhone: async (code: string): Promise<{ message: string }> => {
      const response = await this.client.post<ApiResponse<any>>('/user/phone/verify', { code });
      return response.data.data!;
    },

    // API Keys
    getApiKeys: async (): Promise<ApiKey[]> => {
      const response = await this.client.get<ApiResponse<ApiKey[]>>('/user/api-keys');
      return response.data.data!;
    },

    createApiKey: async (data: CreateApiKeyRequest): Promise<CreateApiKeyResponse> => {
      const response = await this.client.post<ApiResponse<CreateApiKeyResponse>>('/user/api-keys', data);
      return response.data.data!;
    },

    updateApiKey: async (keyId: string, data: { name?: string; ip_whitelist?: string[]; is_active?: boolean }): Promise<void> => {
      await this.client.patch(`/user/api-keys/${keyId}`, data);
    },

    deleteApiKey: async (keyId: string): Promise<void> => {
      await this.client.delete(`/user/api-keys/${keyId}`);
    },

    // Security Events
    getSecurityEvents: async (limit?: number, offset?: number): Promise<{ events: SecurityEvent[]; total: number }> => {
      const response = await this.client.get<ApiResponse<{ events: SecurityEvent[]; total: number }>>('/user/security-events', {
        params: { limit, offset },
      });
      return response.data.data!;
    },

    // Login Attempts
    getLoginAttempts: async (limit?: number): Promise<LoginAttempt[]> => {
      const response = await this.client.get<ApiResponse<LoginAttempt[]>>('/user/login-attempts', {
        params: { limit },
      });
      return response.data.data!;
    },
  };

  // Asset endpoints
  assets = {
    // Public endpoints
    list: async (params?: Record<string, string>): Promise<PaginatedResponse<Asset>> => {
      const response = await this.client.get<ApiResponse<PaginatedResponse<Asset>>>('/assets', { params });
      return response.data.data!;
    },

    get: async (id: string): Promise<any> => {
      const response = await this.client.get<ApiResponse<any>>(`/assets/${id}`);
      return response.data.data!;
    },

    getStats: async (id: string): Promise<any> => {
      const response = await this.client.get<ApiResponse<any>>(`/assets/${id}/stats`);
      return response.data.data!;
    },

    getHolders: async (id: string, params?: Record<string, string | number>): Promise<PaginatedResponse<any>> => {
      const response = await this.client.get<ApiResponse<PaginatedResponse<any>>>(`/assets/${id}/holders`, { params });
      return response.data.data!;
    },

    // Asset application endpoints
    apply: async (data: any): Promise<{ application_id: string; message: string }> => {
      const response = await this.client.post<ApiResponse<{ application_id: string; message: string }>>('/assets/apply', data);
      return response.data.data!;
    },

    getMyApplications: async (params?: Record<string, string>): Promise<PaginatedResponse<any>> => {
      const response = await this.client.get<ApiResponse<PaginatedResponse<any>>>('/assets/my/applications', { params });
      return response.data.data!;
    },

    getMyApplication: async (id: string): Promise<any> => {
      const response = await this.client.get<ApiResponse<any>>(`/assets/my/applications/${id}`);
      return response.data.data!;
    },

    updateApplication: async (id: string, data: any): Promise<void> => {
      await this.client.patch(`/assets/my/applications/${id}`, data);
    },

    submitApplication: async (id: string): Promise<void> => {
      await this.client.post(`/assets/my/applications/${id}/submit`);
    },

    cancelApplication: async (id: string): Promise<void> => {
      await this.client.post(`/assets/my/applications/${id}/cancel`);
    },

    uploadApplicationDocument: async (id: string, file: File, documentType: string): Promise<{ file_key: string; file_url: string }> => {
      const formData = new FormData();
      formData.append('file', file);
      formData.append('document_type', documentType);
      const response = await this.client.post<ApiResponse<{ file_key: string; file_url: string }>>(`/assets/my/applications/${id}/documents`, formData, {
        headers: { 'Content-Type': 'multipart/form-data' },
      });
      return response.data.data!;
    },

    // My assets (as issuer)
    getMyAssets: async (params?: Record<string, string | number>): Promise<PaginatedResponse<Asset>> => {
      const response = await this.client.get<ApiResponse<PaginatedResponse<Asset>>>('/assets/my/list', { params });
      return response.data.data!;
    },

    // My holdings
    getMyHoldings: async (params?: Record<string, string>): Promise<PaginatedResponse<any>> => {
      const response = await this.client.get<ApiResponse<PaginatedResponse<any>>>('/assets/my/holdings', { params });
      return response.data.data!;
    },
  };

  // Trading endpoints
  trading = {
    getPairs: async (): Promise<TradingPair[]> => {
      const response = await this.client.get<ApiResponse<TradingPair[]>>('/trading/pairs');
      return response.data.data!;
    },

    getPair: async (id: string): Promise<TradingPair> => {
      const response = await this.client.get<ApiResponse<TradingPair>>(`/trading/pairs/${id}`);
      return response.data.data!;
    },

    getOrderBook: async (pairId: string, limit?: number): Promise<OrderBook> => {
      const response = await this.client.get<ApiResponse<OrderBook>>(`/trading/pairs/${pairId}/orderbook`, {
        params: { limit },
      });
      return response.data.data!;
    },

    getTicker: async (pairId: string): Promise<Ticker> => {
      const response = await this.client.get<ApiResponse<Ticker>>(`/trading/pairs/${pairId}/ticker`);
      return response.data.data!;
    },

    placeOrder: async (data: Partial<Order>): Promise<{ order_id: string }> => {
      const response = await this.client.post<ApiResponse<{ order_id: string }>>('/trading/orders', data);
      return response.data.data!;
    },

    getOrders: async (params?: Record<string, string>): Promise<PaginatedResponse<Order>> => {
      const response = await this.client.get<ApiResponse<PaginatedResponse<Order>>>('/trading/orders', { params });
      return response.data.data!;
    },

    getOrder: async (id: string): Promise<Order> => {
      const response = await this.client.get<ApiResponse<Order>>(`/trading/orders/${id}`);
      return response.data.data!;
    },

    cancelOrder: async (id: string): Promise<void> => {
      await this.client.delete(`/trading/orders/${id}`);
    },
  };

  // Wallet binding endpoints
  wallet = {
    // Get bound wallets
    getWallets: async (): Promise<Array<{
      id: string;
      address: string;
      chain_id: number;
      label: string;
      is_primary: boolean;
      verified_at: string;
      created_at: string;
    }>> => {
      const response = await this.client.get<ApiResponse<any[]>>('/user/wallets');
      return response.data.data!;
    },

    // Request wallet binding (step 1)
    requestBind: async (address: string, chainId?: number): Promise<{
      message: string;
      nonce: string;
      expires_in: number;
    }> => {
      const response = await this.client.post<ApiResponse<any>>('/user/wallets/request-bind', {
        address,
        chain_id: chainId,
      });
      return response.data.data!;
    },

    // Confirm wallet binding (step 2)
    confirmBind: async (address: string, signature: string, label?: string): Promise<{
      id: string;
      address: string;
      chain_id: number;
      label: string;
      is_primary: boolean;
      message: string;
    }> => {
      const response = await this.client.post<ApiResponse<any>>('/user/wallets/confirm-bind', {
        address,
        signature,
        label,
      });
      return response.data.data!;
    },

    // Unbind wallet
    unbind: async (walletId: string): Promise<void> => {
      await this.client.delete(`/user/wallets/${walletId}`);
    },

    // Set primary wallet
    setPrimary: async (walletId: string): Promise<void> => {
      await this.client.post(`/user/wallets/${walletId}/set-primary`);
    },

    getBalances: async (walletType?: string): Promise<WalletBalance[]> => {
      const response = await this.client.get<ApiResponse<WalletBalance[]>>('/wallet/balances', {
        params: { wallet_type: walletType },
      });
      return response.data.data!;
    },

    getBalance: async (assetId: string): Promise<WalletBalance[]> => {
      const response = await this.client.get<ApiResponse<WalletBalance[]>>(`/wallet/balances/${assetId}`);
      return response.data.data!;
    },

    getDepositAddress: async (assetId: string, network?: string): Promise<{
      id: string;
      address: string;
      memo?: string;
      network: string;
      asset_id: string;
    }> => {
      const response = await this.client.get<ApiResponse<any>>(
        `/wallet/deposit/${assetId}/address`,
        { params: { network } }
      );
      return response.data.data!;
    },

    getDeposits: async (params?: {
      asset_id?: string;
      status?: string;
      page?: number;
      per_page?: number;
    }): Promise<{
      items: Array<{
        id: string;
        asset_id: string;
        asset_symbol: string;
        network: string;
        tx_hash: string;
        amount: string;
        status: string;
        confirmations: number;
        required_confirmations: number;
        created_at: string;
        confirmed_at?: string;
      }>;
      meta: { page: number; per_page: number; total: number; total_pages: number };
    }> => {
      const response = await this.client.get<ApiResponse<any>>('/wallet/deposits', { params });
      return response.data.data!;
    },

    withdraw: async (data: {
      asset_id: string;
      network: string;
      address: string;
      amount: string;
      memo?: string;
      two_factor_code?: string;
      two_factor_method?: 'totp' | 'email' | 'sms';
    }): Promise<{ withdrawal_id: string; message: string }> => {
      const response = await this.client.post<ApiResponse<{ withdrawal_id: string; message: string }>>('/wallet/withdraw', data);
      return response.data.data!;
    },

    getWithdrawals: async (params?: {
      asset_id?: string;
      status?: string;
      page?: number;
      per_page?: number;
    }): Promise<{
      items: Array<{
        id: string;
        asset_id: string;
        asset_symbol: string;
        network: string;
        address: string;
        memo?: string;
        amount: string;
        fee: string;
        status: string;
        tx_hash?: string;
        created_at: string;
        processed_at?: string;
      }>;
      meta: { page: number; per_page: number; total: number; total_pages: number };
    }> => {
      const response = await this.client.get<ApiResponse<any>>('/wallet/withdrawals', { params });
      return response.data.data!;
    },

    cancelWithdrawal: async (withdrawalId: string): Promise<{ message: string }> => {
      const response = await this.client.delete<ApiResponse<{ message: string }>>(`/wallet/withdrawals/${withdrawalId}`);
      return response.data.data!;
    },

    transfer: async (data: {
      from_wallet_type: string;
      to_wallet_type: string;
      asset_id: string;
      amount: string;
    }): Promise<{ transaction_id: string; message: string }> => {
      const response = await this.client.post<ApiResponse<{ transaction_id: string; message: string }>>('/wallet/transfer', data);
      return response.data.data!;
    },

    getTransactions: async (params?: {
      asset_id?: string;
      type?: string;
      page?: number;
      per_page?: number;
    }): Promise<{
      items: Array<{
        id: string;
        asset_id: string;
        asset_symbol: string;
        type: string;
        status: string;
        amount: string;
        fee: string;
        description?: string;
        created_at: string;
      }>;
      meta: { page: number; per_page: number; total: number; total_pages: number };
    }> => {
      const response = await this.client.get<ApiResponse<any>>('/wallet/transactions', { params });
      return response.data.data!;
    },
  };

  // KYC endpoints
  kyc = {
    getStatus: async (): Promise<{ kyc_level: number; application: any }> => {
      const response = await this.client.get<ApiResponse<{ kyc_level: number; application: any }>>('/kyc/status');
      return response.data.data!;
    },

    getApplication: async (id: string): Promise<any> => {
      const response = await this.client.get<ApiResponse<any>>(`/kyc/application/${id}`);
      return response.data.data!;
    },

    submit: async (data: {
      kyc_type: 'personal' | 'institutional';
      legal_name: string;
      id_type: string;
      id_number: string;
      nationality: string;
      date_of_birth?: string;
      address?: string;
      company_name?: string;
      registration_number?: string;
      registered_address?: string;
      business_type?: string;
    }): Promise<{ application_id: string; message: string }> => {
      const response = await this.client.post<ApiResponse<{ application_id: string; message: string }>>('/kyc/submit', data);
      return response.data.data!;
    },

    uploadDocument: async (applicationId: string, file: File, documentType: string): Promise<{ document_id: string }> => {
      const formData = new FormData();
      formData.append('file', file);
      formData.append('document_type', documentType);

      const response = await this.client.post<ApiResponse<{ document_id: string }>>(
        `/kyc/documents/${applicationId}`,
        formData,
        { headers: { 'Content-Type': 'multipart/form-data' } }
      );
      return response.data.data!;
    },

    submitForReview: async (applicationId: string): Promise<{ message: string }> => {
      const response = await this.client.post<ApiResponse<{ message: string }>>(`/kyc/submit-for-review/${applicationId}`);
      return response.data.data!;
    },

    getMarketMakerStatus: async (): Promise<any> => {
      const response = await this.client.get<ApiResponse<any>>('/kyc/market-maker');
      return response.data.data!;
    },

    applyMarketMaker: async (data: {
      company_name: string;
      contact_name: string;
      contact_email: string;
      contact_phone?: string;
      experience_description: string;
      trading_volume_monthly: string;
    }): Promise<{ application_id: string; message: string }> => {
      const response = await this.client.post<ApiResponse<{ application_id: string; message: string }>>('/kyc/market-maker', data);
      return response.data.data!;
    },
  };

  // Bonding Curve endpoints
  bonding = {
    // Get all bonding curves
    list: async (params?: { status?: string; limit?: number; offset?: number }): Promise<{ curves: any[]; stats: any }> => {
      const response = await this.client.get<ApiResponse<{ curves: any[]; stats: any }>>('/bonding/list', { params });
      return response.data.data!;
    },

    // Get bonding curve by asset ID
    getCurve: async (assetId: string): Promise<any> => {
      const response = await this.client.get<ApiResponse<any>>(`/bonding/${assetId}`);
      return response.data.data!;
    },

    // Get current price
    getPrice: async (assetId: string): Promise<{ price: string; supply: string; reserve: string }> => {
      const response = await this.client.get<ApiResponse<{ price: string; supply: string; reserve: string }>>(`/bonding/${assetId}/price`);
      return response.data.data!;
    },

    // Simulate buy
    simulateBuy: async (assetId: string, amount: string): Promise<{
      tokenAmount: string;
      quoteAmount: string;
      averagePrice: string;
      priceImpact: string;
      fee: string;
    }> => {
      const response = await this.client.post<ApiResponse<any>>(`/bonding/${assetId}/simulate-buy`, {
        quote_amount: amount,
      });
      return response.data.data!;
    },

    // Simulate sell
    simulateSell: async (assetId: string, amount: string): Promise<{
      tokenAmount: string;
      quoteAmount: string;
      averagePrice: string;
      priceImpact: string;
      fee: string;
    }> => {
      const response = await this.client.post<ApiResponse<any>>(`/bonding/${assetId}/simulate-sell`, {
        token_amount: amount,
      });
      return response.data.data!;
    },

    // Build buy transaction
    buildBuyTransaction: async (assetId: string, quoteAmount: string, minTokens: string): Promise<{
      to: string;
      data: string;
      value: string;
      gasLimit: string;
      chainId: number;
    }> => {
      const response = await this.client.post<ApiResponse<any>>(`/bonding/${assetId}/buy`, {
        quote_amount: quoteAmount,
        min_tokens: minTokens,
      });
      return response.data.data!;
    },

    // Build sell transaction
    buildSellTransaction: async (assetId: string, tokenAmount: string, minQuote: string): Promise<{
      to: string;
      data: string;
      value: string;
      gasLimit: string;
      chainId: number;
    }> => {
      const response = await this.client.post<ApiResponse<any>>(`/bonding/${assetId}/sell`, {
        token_amount: tokenAmount,
        min_quote: minQuote,
      });
      return response.data.data!;
    },

    // Confirm transaction
    confirmTransaction: async (assetId: string, txHash: string, type: 'buy' | 'sell'): Promise<{
      status: 'success' | 'pending' | 'failed';
      transactionId: string;
    }> => {
      const response = await this.client.post<ApiResponse<any>>(`/bonding/${assetId}/confirm`, {
        tx_hash: txHash,
        type,
      });
      return response.data.data!;
    },

    // Get transaction history
    getHistory: async (assetId: string, params?: { limit?: number; offset?: number }): Promise<{
      transactions: any[];
      total: number;
    }> => {
      const response = await this.client.get<ApiResponse<{ transactions: any[]; total: number }>>(`/bonding/${assetId}/history`, { params });
      return response.data.data!;
    },

    // Get price chart data
    getChart: async (assetId: string, params?: { interval?: string; limit?: number }): Promise<{
      points: Array<{ timestamp: number; price: string; supply: string }>;
    }> => {
      const response = await this.client.get<ApiResponse<{ points: any[] }>>(`/bonding/${assetId}/chart`, { params });
      return response.data.data!;
    },

    // Get progress to DEX migration
    getProgress: async (assetId: string): Promise<{
      progress: number;
      currentRaise: string;
      targetRaise: string;
      estimatedMigration: string | null;
    }> => {
      const response = await this.client.get<ApiResponse<any>>(`/bonding/${assetId}/progress`);
      return response.data.data!;
    },
  };

  // Pool (AMM) endpoints
  pool = {
    // Get all active pools
    list: async (params?: { page?: number; per_page?: number }): Promise<{
      items: any[];
      meta: { page: number; per_page: number; total: number; total_pages: number };
    }> => {
      const response = await this.client.get<ApiResponse<any>>('/pools', { params });
      return response.data.data!;
    },

    // Get pool by asset ID
    getPool: async (assetId: string): Promise<{
      pool: any;
      asset: any;
      stats: any;
      recentTrades: any[];
    }> => {
      const response = await this.client.get<ApiResponse<any>>(`/pools/${assetId}`);
      return response.data.data!;
    },

    // Get current price
    getPrice: async (assetId: string): Promise<{
      price: string;
      priceChange24h: number;
      tokenReserve: string;
      quoteReserve: string;
      updatedAt: string;
    }> => {
      const response = await this.client.get<ApiResponse<any>>(`/pools/${assetId}/price`);
      return response.data.data!;
    },

    // Simulate swap
    simulateSwap: async (assetId: string, tradeType: 'buy' | 'sell', amount: string): Promise<{
      quote: {
        amountIn: string;
        amountOut: string;
        priceImpact: number;
        fee: string;
        executionPrice: string;
        minAmountOut: string;
        route: 'buy' | 'sell';
      };
      pool: { tokenReserve: string; quoteReserve: string; feeRate: number };
    }> => {
      const response = await this.client.post<ApiResponse<any>>(`/pools/${assetId}/simulate-swap`, {
        tradeType,
        amount,
      });
      return response.data.data!;
    },

    // Execute swap
    swap: async (assetId: string, params: {
      tradeType: 'buy' | 'sell';
      amountIn: string;
      minAmountOut?: string;
      slippageTolerance?: number;
    }): Promise<{
      id: string;
      poolId: string;
      tradeType: string;
      inputAmount: string;
      outputAmount: string;
      price: string;
      priceImpact: number;
      fee: string;
    }> => {
      const response = await this.client.post<ApiResponse<any>>(`/pools/${assetId}/swap`, params);
      return response.data.data!;
    },

    // Add liquidity
    addLiquidity: async (assetId: string, params: {
      tokenAmount: string;
      quoteAmount: string;
      minLpTokens?: string;
    }): Promise<{
      id: string;
      poolId: string;
      tokenAmount: string;
      quoteAmount: string;
      lpTokenAmount: string;
    }> => {
      const response = await this.client.post<ApiResponse<any>>(`/pools/${assetId}/add-liquidity`, params);
      return response.data.data!;
    },

    // Remove liquidity
    removeLiquidity: async (assetId: string, params: {
      lpTokenAmount: string;
      minTokenAmount?: string;
      minQuoteAmount?: string;
    }): Promise<{
      id: string;
      poolId: string;
      tokenAmount: string;
      quoteAmount: string;
      lpTokenAmount: string;
    }> => {
      const response = await this.client.post<ApiResponse<any>>(`/pools/${assetId}/remove-liquidity`, params);
      return response.data.data!;
    },

    // Get K-line data
    getKlines: async (assetId: string, params?: {
      interval?: '1m' | '5m' | '15m' | '1h' | '4h' | '1d' | '1w';
      startTime?: string;
      endTime?: string;
      limit?: number;
    }): Promise<Array<{
      time: number;
      open: string;
      high: string;
      low: string;
      close: string;
      volume: string;
    }>> => {
      const response = await this.client.get<ApiResponse<any[]>>(`/pools/${assetId}/klines`, { params });
      return response.data.data!;
    },

    // Get market statistics for charts
    getMarketStats: async (assetId: string): Promise<{
      currentPrice: string;
      priceChange24h: number;
      priceChangeAbsolute: string;
      high24h: string;
      low24h: string;
      volume24h: string;
      trades24h: number;
    }> => {
      const response = await this.client.get<ApiResponse<any>>(`/pools/${assetId}/market-stats`);
      return response.data.data!;
    },

    // Get recent trades
    getTrades: async (assetId: string, limit?: number): Promise<any[]> => {
      const response = await this.client.get<ApiResponse<any[]>>(`/pools/${assetId}/trades`, {
        params: { limit },
      });
      return response.data.data!;
    },

    // Get my position in a pool
    getMyPosition: async (assetId: string): Promise<{
      lp_token_balance: string;
      sharePercent: number;
      tokenValue: string;
      quoteValue: string;
      total_token_deposited: string;
      total_quote_deposited: string;
      total_token_withdrawn: string;
      total_quote_withdrawn: string;
    } | null> => {
      const response = await this.client.get<ApiResponse<any>>(`/pools/${assetId}/my-position`);
      return response.data.data;
    },

    // Get all my positions
    getMyPositions: async (): Promise<Array<{
      pool: any;
      position: {
        lp_token_balance: string;
        share_percent: number;
        token_value: string;
        quote_value: string;
      };
    }>> => {
      const response = await this.client.get<ApiResponse<any[]>>('/pools/my/positions');
      return response.data.data!;
    },

    // Get my history in a pool
    getMyHistory: async (assetId: string, limit?: number): Promise<{
      liquidityEvents: any[];
      swaps: any[];
    }> => {
      const response = await this.client.get<ApiResponse<any>>(`/pools/${assetId}/my-history`, {
        params: { limit },
      });
      return response.data.data!;
    },
  };

  // Limit Order Trading endpoints
  limitOrders = {
    // Get trading pairs for limit orders
    getPairs: async (params?: { status?: string }): Promise<any[]> => {
      const response = await this.client.get<ApiResponse<any[]>>('/limit-orders/pairs', { params });
      return response.data.data!;
    },

    // Get trading pair details
    getPair: async (pairId: string): Promise<any> => {
      const response = await this.client.get<ApiResponse<any>>(`/limit-orders/pairs/${pairId}`);
      return response.data.data!;
    },

    // Get order book snapshot
    getOrderBook: async (pairId: string, depth?: number): Promise<{
      bids: Array<{ price: string; quantity: string; count: number }>;
      asks: Array<{ price: string; quantity: string; count: number }>;
      lastTradePrice: string;
      lastTradeQuantity: string;
      timestamp: number;
      sequence: number;
    }> => {
      const response = await this.client.get<ApiResponse<any>>(`/limit-orders/orderbook/${pairId}`, {
        params: { depth },
      });
      return response.data.data!;
    },

    // Submit a new order
    submitOrder: async (params: {
      tradingPairId: string;
      side: 'buy' | 'sell';
      orderType: 'limit' | 'market' | 'stop_limit' | 'stop_market' | 'take_profit_limit' | 'take_profit_market' | 'iceberg' | 'hidden' | 'post_only';
      timeInForce?: 'GTC' | 'IOC' | 'FOK' | 'GTD' | 'DAY' | 'POST_ONLY';
      price?: string;
      stopPrice?: string;
      quantity: string;
      displayQuantity?: string;
      clientOrderId?: string;
      expiresAt?: string;
    }): Promise<{
      orderId: string;
      clientOrderId: string;
      status: string;
      filledQuantity: string;
      averagePrice: string;
      trades: Array<{
        id: string;
        price: string;
        quantity: string;
        value: string;
        takerSide: string;
        timestamp: number;
      }>;
    }> => {
      const response = await this.client.post<ApiResponse<any>>('/limit-orders/orders', params);
      return response.data.data!;
    },

    // Cancel an order
    cancelOrder: async (orderId: string): Promise<{ message: string }> => {
      const response = await this.client.delete<ApiResponse<any>>(`/limit-orders/orders/${orderId}`);
      return response.data.data!;
    },

    // Cancel all orders for a trading pair
    cancelAllOrders: async (tradingPairId: string, side?: 'buy' | 'sell'): Promise<{ cancelledCount: number }> => {
      const response = await this.client.post<ApiResponse<any>>('/limit-orders/orders/cancel-all', {
        tradingPairId,
        side,
      });
      return response.data.data!;
    },

    // Get user's orders
    getOrders: async (params?: {
      tradingPairId?: string;
      status?: string;
      side?: 'buy' | 'sell';
      limit?: number;
      offset?: number;
    }): Promise<{
      orders: any[];
      total: number;
    }> => {
      const response = await this.client.get<ApiResponse<any>>('/limit-orders/orders', { params });
      return response.data.data!;
    },

    // Get order details
    getOrder: async (orderId: string): Promise<any> => {
      const response = await this.client.get<ApiResponse<any>>(`/limit-orders/orders/${orderId}`);
      return response.data.data!;
    },

    // Get user's trade history
    getTrades: async (params?: {
      tradingPairId?: string;
      side?: 'buy' | 'sell';
      limit?: number;
      offset?: number;
    }): Promise<{
      trades: any[];
      total: number;
    }> => {
      const response = await this.client.get<ApiResponse<any>>('/limit-orders/trades', { params });
      return response.data.data!;
    },

    // Get recent trades for a trading pair (public)
    getRecentTrades: async (pairId: string, limit?: number): Promise<any[]> => {
      const response = await this.client.get<ApiResponse<any[]>>(`/limit-orders/pairs/${pairId}/trades`, {
        params: { limit },
      });
      return response.data.data!;
    },

    // Get ticker for a trading pair
    getTicker: async (pairId: string): Promise<{
      symbol: string;
      lastPrice: string;
      priceChange24h: string;
      priceChangePercent24h: string;
      high24h: string;
      low24h: string;
      volume24h: string;
      quoteVolume24h: string;
      tradeCount24h: number;
      bestBid: string;
      bestAsk: string;
      spread: string;
    }> => {
      const response = await this.client.get<ApiResponse<any>>(`/limit-orders/pairs/${pairId}/ticker`);
      return response.data.data!;
    },

    // Get all tickers
    getAllTickers: async (): Promise<any[]> => {
      const response = await this.client.get<ApiResponse<any[]>>('/limit-orders/tickers');
      return response.data.data!;
    },

    // WebSocket connection URL
    getWebSocketUrl: (pairId: string): string => {
      const baseUrl = import.meta.env.VITE_WS_URL || import.meta.env.VITE_API_URL?.replace('http', 'ws') || '';
      return `${baseUrl}/limit-orders/ws/${pairId}`;
    },
  };

  // Revenue Distribution endpoints
  revenue = {
    // Submit revenue report (for asset issuers)
    submitReport: async (data: {
      assetId: string;
      periodType: 'monthly' | 'quarterly' | 'annual' | 'custom';
      periodStart: string;
      periodEnd: string;
      grossRevenue: string;
      netRevenue: string;
      distributableAmount: string;
      currency?: string;
      evidenceUrl?: string;
      evidenceHash?: string;
      notes?: string;
    }): Promise<any> => {
      const response = await this.client.post<ApiResponse<any>>('/revenue/report', data);
      return response.data.data!;
    },

    // Get my revenue reports (as issuer)
    getMyReports: async (params?: {
      assetId?: string;
      status?: string;
      page?: number;
      pageSize?: number;
    }): Promise<{
      items: any[];
      total: number;
      page: number;
      pageSize: number;
      totalPages: number;
    }> => {
      const response = await this.client.get<ApiResponse<any>>('/revenue/reports', { params });
      return response.data.data!;
    },

    // Get single revenue report
    getReport: async (reportId: string): Promise<any> => {
      const response = await this.client.get<ApiResponse<any>>(`/revenue/reports/${reportId}`);
      return response.data.data!;
    },

    // Update revenue report
    updateReport: async (id: string, data: {
      periodType?: string;
      periodStart?: string;
      periodEnd?: string;
      grossRevenue?: string;
      netRevenue?: string;
      distributableAmount?: string;
      currency?: string;
      evidenceUrl?: string;
      evidenceHash?: string;
      notes?: string;
    }): Promise<any> => {
      const response = await this.client.put<ApiResponse<any>>(`/revenue/reports/${id}`, data);
      return response.data.data!;
    },

    // Get asset distribution history (public)
    getDistributions: async (assetId: string, params?: {
      page?: number;
      pageSize?: number;
    }): Promise<{
      items: any[];
      total: number;
      page: number;
      pageSize: number;
      totalPages: number;
    }> => {
      const response = await this.client.get<ApiResponse<any>>(`/revenue/distributions/${assetId}`, { params });
      return response.data.data!;
    },

    // Get asset dividend stats
    getAssetStats: async (assetId: string): Promise<{
      assetId: string;
      totalDistributed: string;
      totalFees: string;
      distributionCount: number;
      holderCount: number;
      lastDistributionAt: string | null;
      nextDistributionAt: string | null;
    }> => {
      const response = await this.client.get<ApiResponse<any>>(`/revenue/distributions/${assetId}/stats`);
      return response.data.data!;
    },
  };

  // User Dividends endpoints
  dividends = {
    // Get my dividend records
    getMyDividends: async (params?: {
      assetId?: string;
      status?: string;
      page?: number;
      pageSize?: number;
    }): Promise<{
      items: any[];
      total: number;
      page: number;
      pageSize: number;
      totalPages: number;
    }> => {
      const response = await this.client.get<ApiResponse<any>>('/user/dividends', { params });
      return response.data.data!;
    },

    // Get pending dividends
    getPending: async (): Promise<{
      items: any[];
      totalPending: string;
    }> => {
      const response = await this.client.get<ApiResponse<any>>('/user/dividends/pending');
      return response.data.data!;
    },

    // Get dividend summary
    getSummary: async (): Promise<{
      totalPending: string;
      totalClaimed: string;
      totalReceived: string;
      pendingCount: number;
      assetBreakdown: Array<{
        assetId: string;
        assetName: string;
        assetSymbol: string;
        pending: string;
        claimed: string;
      }>;
    }> => {
      const response = await this.client.get<ApiResponse<any>>('/user/dividends/summary');
      return response.data.data!;
    },

    // Claim single dividend
    claim: async (dividendId: string): Promise<{
      message: string;
      dividend: any;
    }> => {
      const response = await this.client.post<ApiResponse<any>>(`/user/dividends/${dividendId}/claim`);
      return response.data.data!;
    },

    // Claim all dividends
    claimAll: async (assetId?: string): Promise<{
      message: string;
      claimed: number;
      totalAmount: string;
    }> => {
      const response = await this.client.post<ApiResponse<any>>('/user/dividends/claim-all', { assetId });
      return response.data.data!;
    },

    // Get revenue stats
    getStats: async (assetId?: string): Promise<any[]> => {
      const response = await this.client.get<ApiResponse<any[]>>('/user/dividends/stats', {
        params: { assetId },
      });
      return response.data.data!;
    },
  };

  // Issuer Rebates endpoints
  issuerRebates = {
    // Get rebate summary
    getSummary: async (): Promise<any> => {
      const response = await this.client.get<ApiResponse<any>>('/issuer/rebates/summary');
      return response.data.data!;
    },

    // Get rebate list
    getList: async (params?: { page?: number; per_page?: number; asset_id?: string }): Promise<any> => {
      const response = await this.client.get<ApiResponse<any>>('/issuer/rebates', { params });
      return response.data.data!;
    },

    // Get rebate chart data
    getChart: async (days?: number): Promise<any[]> => {
      const response = await this.client.get<ApiResponse<any[]>>('/issuer/rebates/chart', {
        params: { days },
      });
      return response.data.data!;
    },
  };

  // LP Token Claims endpoints (for Bonding Curve to AMM migration)
  lpClaims = {
    // Get my claimable LP tokens
    getMyClaims: async (): Promise<{
      claims: Array<{
        id: string;
        migrationId: string;
        assetId: string;
        assetName: string;
        assetSymbol: string;
        lpTokenAmount: string;
        status: 'pending' | 'claimed' | 'expired';
        expiresAt: string | null;
        claimedAt: string | null;
        createdAt: string;
      }>;
      totalPending: string;
      totalClaimed: string;
    }> => {
      const response = await this.client.get<ApiResponse<any>>('/bonding/lp-claims/my');
      return response.data.data!;
    },

    // Claim LP tokens
    claim: async (claimId: string): Promise<{
      message: string;
      claim: any;
    }> => {
      const response = await this.client.post<ApiResponse<any>>(`/bonding/lp-claims/${claimId}/claim`);
      return response.data.data!;
    },

    // Claim all pending LP tokens
    claimAll: async (): Promise<{
      message: string;
      claimedCount: number;
      totalAmount: string;
    }> => {
      const response = await this.client.post<ApiResponse<any>>('/bonding/lp-claims/claim-all');
      return response.data.data!;
    },

    // Get claim details
    getClaim: async (claimId: string): Promise<any> => {
      const response = await this.client.get<ApiResponse<any>>(`/bonding/lp-claims/${claimId}`);
      return response.data.data!;
    },

    // Get migration info for an asset
    getMigrationInfo: async (assetId: string): Promise<{
      migration: any;
      lpClaimStats: {
        totalEligible: number;
        totalClaimed: number;
        totalPending: number;
        totalLpTokens: string;
        claimedLpTokens: string;
      };
    } | null> => {
      const response = await this.client.get<ApiResponse<any>>(`/bonding/${assetId}/migration-info`);
      return response.data.data;
    },
  };

  // Admin Revenue endpoints
  adminRevenue = {
    // Get overview
    getOverview: async (): Promise<{
      reportStats: any;
      distributionStats: any;
      recentReports: any[];
      recentDistributions: any[];
    }> => {
      const response = await this.client.get<ApiResponse<any>>('/admin/revenue/overview');
      return response.data.data!;
    },

    // Get revenue reports
    getReports: async (params?: {
      assetId?: string;
      status?: string;
      reporterId?: string;
      page?: number;
      pageSize?: number;
    }): Promise<{
      items: any[];
      total: number;
      page: number;
      pageSize: number;
      totalPages: number;
    }> => {
      const response = await this.client.get<ApiResponse<any>>('/admin/revenue/reports', { params });
      return response.data.data!;
    },

    // Get report details
    getReport: async (id: string): Promise<{
      report: any;
      asset: any;
      reporter: any;
      reviewer: any;
      historyReports: any[];
    }> => {
      const response = await this.client.get<ApiResponse<any>>(`/admin/revenue/reports/${id}`);
      return response.data.data!;
    },

    // Start reviewing report
    startReview: async (id: string): Promise<any> => {
      const response = await this.client.post<ApiResponse<any>>(`/admin/revenue/reports/${id}/review`);
      return response.data.data!;
    },

    // Approve report
    approveReport: async (id: string, reviewNotes?: string): Promise<{
      message: string;
      report: any;
    }> => {
      const response = await this.client.post<ApiResponse<any>>(`/admin/revenue/reports/${id}/approve`, { reviewNotes });
      return response.data.data!;
    },

    // Reject report
    rejectReport: async (id: string, rejectReason: string, reviewNotes?: string): Promise<{
      message: string;
      report: any;
    }> => {
      const response = await this.client.post<ApiResponse<any>>(`/admin/revenue/reports/${id}/reject`, {
        rejectReason,
        reviewNotes,
      });
      return response.data.data!;
    },

    // Create snapshot
    createSnapshot: async (assetId: string, revenueReportId?: string): Promise<{
      message: string;
      snapshot: any;
    }> => {
      const response = await this.client.post<ApiResponse<any>>('/admin/revenue/snapshots', {
        assetId,
        revenueReportId,
      });
      return response.data.data!;
    },

    // Get snapshots
    getSnapshots: async (assetId: string, params?: {
      page?: number;
      pageSize?: number;
    }): Promise<{
      items: any[];
      total: number;
      page: number;
      pageSize: number;
      totalPages: number;
    }> => {
      const response = await this.client.get<ApiResponse<any>>(`/admin/revenue/snapshots/${assetId}`, { params });
      return response.data.data!;
    },

    // Get snapshot details
    getSnapshotDetail: async (id: string): Promise<{
      snapshot: any;
      details: any[];
    }> => {
      const response = await this.client.get<ApiResponse<any>>(`/admin/revenue/snapshots/detail/${id}`);
      return response.data.data!;
    },

    // Get distributions
    getDistributions: async (params?: {
      assetId?: string;
      status?: string;
      page?: number;
      pageSize?: number;
    }): Promise<{
      items: any[];
      total: number;
      page: number;
      pageSize: number;
      totalPages: number;
    }> => {
      const response = await this.client.get<ApiResponse<any>>('/admin/revenue/distributions', { params });
      return response.data.data!;
    },

    // Create distribution
    createDistribution: async (data: {
      assetId: string;
      revenueReportId: string;
      snapshotId: string;
      totalAmount: string;
      minAmountThreshold?: string;
      scheduledAt?: string;
    }): Promise<{
      message: string;
      distribution: any;
    }> => {
      const response = await this.client.post<ApiResponse<any>>('/admin/revenue/distributions', data);
      return response.data.data!;
    },

    // Get distribution details
    getDistribution: async (id: string): Promise<{
      distribution: any;
      asset: any;
      report: any;
      snapshot: any;
    }> => {
      const response = await this.client.get<ApiResponse<any>>(`/admin/revenue/distributions/${id}`);
      return response.data.data!;
    },

    // Calculate distribution
    calculateDistribution: async (id: string): Promise<{
      message: string;
      distribution: any;
    }> => {
      const response = await this.client.post<ApiResponse<any>>(`/admin/revenue/distributions/${id}/calculate`);
      return response.data.data!;
    },

    // Execute distribution
    executeDistribution: async (id: string): Promise<{
      message: string;
      distribution: any;
    }> => {
      const response = await this.client.post<ApiResponse<any>>(`/admin/revenue/distributions/${id}/execute`);
      return response.data.data!;
    },

    // Get distribution progress
    getDistributionProgress: async (id: string): Promise<{
      distribution: any;
      progress: {
        total: number;
        processed: number;
        success: number;
        failed: number;
        pending: number;
        percentage: number;
      };
    }> => {
      const response = await this.client.get<ApiResponse<any>>(`/admin/revenue/distributions/${id}/progress`);
      return response.data.data!;
    },

    // Get distribution details list
    getDistributionDetails: async (id: string, params?: {
      page?: number;
      pageSize?: number;
    }): Promise<{
      items: any[];
      total: number;
      page: number;
      pageSize: number;
      totalPages: number;
    }> => {
      const response = await this.client.get<ApiResponse<any>>(`/admin/revenue/distributions/${id}/details`, { params });
      return response.data.data!;
    },

    // Get dividend configs
    getConfigs: async (params?: {
      page?: number;
      pageSize?: number;
    }): Promise<{
      items: any[];
      total: number;
      page: number;
      pageSize: number;
      totalPages: number;
    }> => {
      const response = await this.client.get<ApiResponse<any>>('/admin/revenue/configs', { params });
      return response.data.data!;
    },

    // Get config for asset
    getConfig: async (assetId: string): Promise<any> => {
      const response = await this.client.get<ApiResponse<any>>(`/admin/revenue/configs/${assetId}`);
      return response.data.data!;
    },

    // Update config
    updateConfig: async (assetId: string, data: {
      distributionFrequency?: string;
      distributionDay?: number;
      nextDistributionDate?: string;
      distributionRatio?: number;
      reserveRatio?: number;
      minHoldingBalance?: string;
      minHoldingDuration?: number;
      platformFeeRate?: number;
      autoDistribute?: boolean;
    }): Promise<{
      message: string;
      config: any;
    }> => {
      const response = await this.client.put<ApiResponse<any>>(`/admin/revenue/configs/${assetId}`, data);
      return response.data.data!;
    },

    // Get asset stats
    getAssetStats: async (assetId: string): Promise<any> => {
      const response = await this.client.get<ApiResponse<any>>(`/admin/revenue/stats/${assetId}`);
      return response.data.data!;
    },

    // Get assets list (for config management)
    getAssets: async (params?: { status?: string; pageSize?: number }): Promise<{
      items: any[];
      total: number;
    }> => {
      const response = await this.client.get<ApiResponse<any>>('/admin/assets', { params });
      return response.data.data!;
    },

    // Upsert dividend config
    upsertConfig: async (data: {
      assetId: string;
      platformFeeRate?: string;
      minDistributionAmount?: string;
      autoDistribute?: boolean;
      distributionDay?: number;
      isActive?: boolean;
    }): Promise<{
      message: string;
      config: any;
    }> => {
      const response = await this.client.post<ApiResponse<any>>('/admin/revenue/configs', data);
      return response.data.data!;
    },
  };

  // Generic request methods for components using api.get/post directly
  async get<T = any>(url: string, config?: any): Promise<{ data: ApiResponse<T> }> {
    return this.client.get<ApiResponse<T>>(url, config);
  }

  async post<T = any>(url: string, data?: any, config?: any): Promise<{ data: ApiResponse<T> }> {
    return this.client.post<ApiResponse<T>>(url, data, config);
  }

  async put<T = any>(url: string, data?: any, config?: any): Promise<{ data: ApiResponse<T> }> {
    return this.client.put<ApiResponse<T>>(url, data, config);
  }

  async patch<T = any>(url: string, data?: any, config?: any): Promise<{ data: ApiResponse<T> }> {
    return this.client.patch<ApiResponse<T>>(url, data, config);
  }

  async delete<T = any>(url: string, config?: any): Promise<{ data: ApiResponse<T> }> {
    return this.client.delete<ApiResponse<T>>(url, config);
  }
}

export const api = new ApiClient();

// Default export for convenience
export default api;
